import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  getRedirectResult,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { auth } from '../../api/firebase';
import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Eye, EyeOff } from 'lucide-react';
import racketImg from '../../assets/images/racket.webp';
import logoImg from '../../assets/images/logo.svg';

const schema = z.object({
  email: z.string().email({ message: 'Email inválido, por favor conferir!' }),
  password: z.string().min(6, { message: 'A senha precisa ter pelo menos 6 caracteres.' }),
});

function Login() {
  const [loginError, setLoginError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const googleProvider = new GoogleAuthProvider();
  const navigate = useNavigate();

  const loginWithGoogle = async () => {
    try {
      signInWithPopup(auth, googleProvider)
        .then((result: any) => {
          localStorage.setItem('accessToken', result.user.accessToken);
          return navigate('/videos');
        })
        .catch((e) => console.error(e));
    } catch (e) {
      console.error('Error during sign-in:', e);
    }
  };

  const loginWithEmail = async (data: any) => {
    const { email, password } = data;
    try {
      signInWithEmailAndPassword(auth, email, password)
        .then((result: any) => {
          localStorage.setItem('accessToken', result.user.accessToken);
          return navigate('/videos');
        })
        .catch(() => setLoginError(true));
    } catch (e) {
      setLoginError(true);
      console.error(e);
    }
  };

  setTimeout(() => {
    getRedirectResult(auth)
      .then((result: any) => {
        if (result) {
          localStorage.setItem('accessToken', result.user.accessToken);
        }
      })
      .catch((error) => console.error('Error during sign-in:', error));
  }, 30000);

  useEffect(() => {
    return () => setLoginError(false);
  }, []);

  const handleForgotPassword = (email: string) => {
    if (!email) {
      alert('Por favor, insira seu email antes de solicitar a redefinição de senha.');
      return;
    }
    sendPasswordResetEmail(auth, email)
      .then(() => alert('Verifique seu email para redefinir a senha.'))
      .catch((error) => alert('Erro ao enviar o email de redefinição: ' + error.message));
  };

  const {
    handleSubmit,
    register,
    formState: { errors },
    getValues,
  } = useForm({ resolver: zodResolver(schema) });

  return (
    <div className="w-full min-h-screen md:grid md:grid-cols-2" style={{ backgroundColor: '#0a0a0a' }}>
      {/* Form side */}
      <div className="flex h-screen items-center justify-center p-6 md:h-auto md:p-0 md:py-12">
        <div className="mx-auto grid w-[350px] gap-2">
          <form onSubmit={handleSubmit(loginWithEmail)} className="flex flex-col gap-6">
            <div className="flex flex-col items-center gap-2 text-center">
              <img src={logoImg} alt="Apertai" style={{ width: '100px' }} className="mb-2" />
              <h1 className="text-2xl font-bold text-white">Entrar na sua conta</h1>
              <p className="text-sm text-zinc-500">Insira seu email e senha para acessar</p>
            </div>
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" placeholder="seu@email.com" {...register('email')} />
                {errors.email?.message && (
                  <p className="text-red-400 text-xs">{String(errors.email.message)}</p>
                )}
              </div>
              <div className="grid gap-2">
                <Label htmlFor="password">Senha</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="******"
                    {...register('password')}
                    style={{ paddingRight: '2.5rem' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex h-full w-10 items-center justify-center text-zinc-500 hover:text-white bg-transparent border-none cursor-pointer"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {errors.password?.message && (
                  <p className="text-red-400 text-xs">{String(errors.password.message)}</p>
                )}
              </div>
              {loginError && (
                <p className="text-red-400 text-sm text-center">Usuário ou senha incorretos!</p>
              )}
              <button
                type="button"
                onClick={() => handleForgotPassword(getValues('email'))}
                className="text-orange-400 text-xs text-right bg-transparent border-none cursor-pointer p-0 hover:underline"
              >
                Esqueci a senha
              </button>
              <button
                type="submit"
                className="w-full h-10 rounded-lg font-semibold text-white border-none cursor-pointer text-sm transition-all hover:scale-[1.02]"
                style={{ background: 'linear-gradient(135deg, #e57605, #f29b2a)' }}
              >
                Entrar
              </button>
            </div>
          </form>

          <div className="text-center text-sm text-zinc-500 mt-2">
            Não tem uma conta?{' '}
            <Link to="/signup" className="text-orange-400 hover:underline no-underline">
              Cadastre-se
            </Link>
          </div>

          <div className="relative text-center text-sm my-2">
            <div className="absolute inset-0 top-1/2 border-t border-zinc-800" />
            <span className="relative z-10 px-2 text-zinc-600" style={{ backgroundColor: '#0a0a0a' }}>
              Ou continue com
            </span>
          </div>

          <button
            type="button"
            onClick={loginWithGoogle}
            className="w-full h-10 rounded-lg font-medium text-white border border-zinc-700 cursor-pointer text-sm flex items-center justify-center gap-2 hover:border-zinc-600 transition-colors"
            style={{ backgroundColor: 'transparent' }}
          >
            <img
              src="https://www.svgrepo.com/show/475656/google-color.svg"
              alt="Google"
              className="h-4 w-4"
            />
            Continuar com Google
          </button>

          <p className="text-zinc-700 text-xs text-center mt-4">
            Ao continuar, você concorda com nossos termos de serviço e políticas de uso.
          </p>
        </div>
      </div>

      {/* Image side */}
      <div
        className="hidden md:block relative bg-cover bg-center"
        style={{ backgroundImage: `url(${racketImg})` }}
      >
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(to top, #0a0a0a, transparent 40%)' }}
        />
        <div className="relative z-10 flex h-full flex-col items-center justify-end p-6 pb-12">
          <p className="text-lg font-medium text-white text-center">
            "Seus vídeos em alta qualidade, assim como suas jogadas."
          </p>
          <span className="text-sm text-zinc-400 mt-2">— Apertai</span>
        </div>
      </div>
    </div>
  );
}

export default Login;
